import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  RotateCw,
  Copy,
  Check,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  FileText,
  Layers,
  ChevronDown,
  ChevronRight,
  Code2,
  ArrowDownLeft,
  ArrowUpRight,
  User,
  Cpu,
  RefreshCcw,
} from 'lucide-react';
import { getSapSyncLogByIdAPI, retrySapSyncLogAPI } from '../../../api/sapSync';
import type { SapSyncLogItem } from '../../../types/sapSync';

interface SapLogDetailDrawerProps {
  logId: number | string | null;
  isOpen: boolean;
  onClose: () => void;
  onRetrySuccess?: () => void;
}

export const SapLogDetailDrawer: React.FC<SapLogDetailDrawerProps> = ({
  logId,
  isOpen,
  onClose,
  onRetrySuccess,
}) => {
  const [log, setLog] = useState<SapSyncLogItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const [copiedFile, setCopiedFile] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);
  const [isJsonOpen, setIsJsonOpen] = useState(true);

  const fetchLogDetail = useCallback(async () => {
    if (!logId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getSapSyncLogByIdAPI(logId);
      if (res && res.data) {
        setLog(res.data);
      } else {
        setLog(res as any);
      }
    } catch (err: any) {
      console.error('Failed to load log detail:', err);
      setError(err.message || 'Failed to load SAP sync log details');
    } finally {
      setLoading(false);
    }
  }, [logId]);

  useEffect(() => {
    if (isOpen && logId) {
      fetchLogDetail();
    }
  }, [isOpen, logId, fetchLogDetail]);

  if (!isOpen) return null;

  const handleCopyFile = (text?: string | null) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedFile(true);
    setTimeout(() => setCopiedFile(false), 2000);
  };

  const handleCopyJson = (content: any) => {
    if (!content) return;
    const jsonStr = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    navigator.clipboard.writeText(jsonStr);
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 2000);
  };

  const handleRetry = async () => {
    if (!logId) return;
    setRetrying(true);
    setRetryMessage(null);
    try {
      const res = await retrySapSyncLogAPI(logId);
      setRetryMessage(res.message || 'Retry job dispatched successfully!');
      if (onRetrySuccess) onRetrySuccess();
      // Refetch detail after retry dispatch
      fetchLogDetail();
    } catch (err: any) {
      console.error('Retry error:', err);
      setError(err.message || 'Failed to retry sync log');
    } finally {
      setRetrying(false);
    }
  };

  const formatDuration = (ms?: number | null, raw?: string | number) => {
    if (raw !== undefined && raw !== null && typeof raw === 'string') return raw;
    if (ms === undefined || ms === null) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const statusNorm = (log?.status || '').toUpperCase();
  const isSuccess = statusNorm === 'SUCCESS' || statusNorm === 'COMPLETED';
  const isPartial = statusNorm.includes('PARTIAL');
  const isFailed = statusNorm === 'FAILED' || statusNorm === 'ERROR';

  const totalRec = log?.totalRecords ?? 0;
  const successRec = log?.successRecords ?? log?.syncedRecords ?? 0;
  const failedRec = log?.failedRecords ?? 0;
  const successPercent = totalRec > 0 ? Math.round((successRec / totalRec) * 100) : isSuccess ? 100 : 0;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex justify-end z-50 animate-fadeIn">
      <div className="bg-white w-full max-w-2xl h-full shadow-2xl flex flex-col overflow-hidden animate-slideLeft">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/90">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center font-mono font-bold text-sm">
              SAP
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-800">
                  Run #{log?.runId || log?.id || logId}
                </h3>
                <span className="text-xs text-slate-400 font-mono">ID: {log?.id || logId}</span>
              </div>
              <p className="text-xs text-slate-500 font-medium">SAP Sync Execution Details</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchLogDetail}
              disabled={loading}
              title="Refresh log"
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 rounded-lg transition-colors"
            >
              <RotateCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Drawer Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {loading && !log ? (
            <div className="py-20 flex flex-col items-center justify-center text-slate-400">
              <RotateCw className="w-8 h-8 animate-spin text-blue-500 mb-3" />
              <p className="text-xs font-semibold">Loading sync run details...</p>
            </div>
          ) : error && !log ? (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs">
              <div className="font-semibold mb-1">Failed to load run details</div>
              <p>{error}</p>
            </div>
          ) : log ? (
            <>
              {/* 1. Status Banner & Quick Retry */}
              <div
                className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                  isSuccess
                    ? 'bg-emerald-50/80 border-emerald-200'
                    : isPartial
                    ? 'bg-amber-50/80 border-amber-200'
                    : 'bg-rose-50/80 border-rose-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  {isSuccess ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                  ) : isPartial ? (
                    <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
                  ) : (
                    <XCircle className="w-6 h-6 text-rose-600 shrink-0" />
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h4
                        className={`text-sm font-bold uppercase tracking-wider ${
                          isSuccess
                            ? 'text-emerald-800'
                            : isPartial
                            ? 'text-amber-800'
                            : 'text-rose-800'
                        }`}
                      >
                        {log.status || 'UNKNOWN'}
                      </h4>
                      {log.retryCount ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
                          Retry #{log.retryCount}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {isSuccess
                        ? 'Job executed and all records synchronized successfully.'
                        : isPartial
                        ? 'Job finished with partial row-level validation errors.'
                        : 'Job failed during execution or connection phase.'}
                    </p>
                  </div>
                </div>

                {/* 1-Click Retry Button */}
                {(isPartial || isFailed) && (
                  <button
                    onClick={handleRetry}
                    disabled={retrying}
                    className="shrink-0 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold shadow-xs flex items-center justify-center gap-1.5 transition-all disabled:opacity-50"
                  >
                    <RefreshCcw className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} />
                    {retrying ? 'Retrying...' : 'Retry Sync'}
                  </button>
                )}
              </div>

              {retryMessage && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs font-semibold text-blue-800 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                  {retryMessage}
                </div>
              )}

              {/* 2. Metadata Badges Grid */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80">
                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">
                  Job Metadata & Execution
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5 text-xs">
                  {/* Job Type */}
                  <div>
                    <span className="text-slate-400 block font-medium text-[11px]">Job Type</span>
                    <span className="font-mono font-semibold text-slate-800 break-all">
                      {log.jobType || '-'}
                    </span>
                  </div>

                  {/* Direction */}
                  <div>
                    <span className="text-slate-400 block font-medium text-[11px]">Direction</span>
                    <span
                      className={`inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded-md text-[11px] mt-0.5 ${
                        log.direction === 'INBOUND'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {log.direction === 'INBOUND' ? (
                        <ArrowDownLeft className="w-3 h-3" />
                      ) : (
                        <ArrowUpRight className="w-3 h-3" />
                      )}
                      {log.direction || 'INBOUND'}
                    </span>
                  </div>

                  {/* Triggered By */}
                  <div>
                    <span className="text-slate-400 block font-medium text-[11px]">Triggered By</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-slate-700 mt-0.5">
                      {log.triggeredBy === 'CRON' ? (
                        <Cpu className="w-3.5 h-3.5 text-slate-500" />
                      ) : (
                        <User className="w-3.5 h-3.5 text-slate-500" />
                      )}
                      {log.triggeredBy || 'CRON'}
                    </span>
                  </div>

                  {/* Execution Duration */}
                  <div>
                    <span className="text-slate-400 block font-medium text-[11px]">Duration</span>
                    <span className="font-semibold text-slate-800 flex items-center gap-1 mt-0.5 font-mono">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      {formatDuration(log.durationMs ?? log.executionDurationMs, log.executionDuration)}
                    </span>
                  </div>

                  {/* Started At */}
                  <div>
                    <span className="text-slate-400 block font-medium text-[11px]">Started At</span>
                    <span className="font-medium text-slate-700">
                      {formatDate(log.startedAt || log.createdAt)}
                    </span>
                  </div>

                  {/* Finished / Completed At */}
                  <div>
                    <span className="text-slate-400 block font-medium text-[11px]">Finished At</span>
                    <span className="font-medium text-slate-700">
                      {formatDate(log.finishedAt || log.completedAt)}
                    </span>
                  </div>
                </div>

                {/* File Path if available */}
                {log.filePath && (
                  <div className="mt-3 pt-2.5 border-t border-slate-200/60 text-xs">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold block">Remote SFTP File Path</span>
                    <span className="font-mono text-slate-700 text-[11px] break-all block mt-0.5">
                      {log.filePath}
                    </span>
                  </div>
                )}

                {/* File Name with 1-Click Copy */}
                {log.fileName && (
                  <div className="mt-3 pt-2.5 border-t border-slate-200/80 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                      <div className="truncate">
                        <span className="text-[10px] text-slate-400 uppercase font-semibold block">File Name</span>
                        <span className="text-xs font-mono font-medium text-slate-800 truncate block">
                          {log.fileName}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCopyFile(log.fileName)}
                      className="px-2.5 py-1 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-200 rounded-lg flex items-center gap-1 transition-colors shrink-0"
                      title="Copy file name"
                    >
                      {copiedFile ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="text-emerald-600 font-semibold">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* 3. Records Gauge / Count Card */}
              <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <Layers className="w-4 h-4 text-indigo-500" />
                    Records Processed
                  </span>
                  <span className="text-xs font-bold font-mono text-slate-800">
                    {successRec} / {totalRec} ({successPercent}%)
                  </span>
                </div>
                {/* Progress bar */}
                <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden flex">
                  <div
                    className="bg-emerald-500 h-full transition-all duration-300"
                    style={{ width: `${totalRec > 0 ? (successRec / totalRec) * 100 : isSuccess ? 100 : 0}%` }}
                    title={`${successRec} Successful`}
                  />
                  {failedRec > 0 && (
                    <div
                      className="bg-rose-500 h-full transition-all duration-300"
                      style={{ width: `${(failedRec / totalRec) * 100}%` }}
                      title={`${failedRec} Failed`}
                    />
                  )}
                </div>
                <div className="mt-2.5 flex items-center justify-between text-xs font-medium text-slate-500">
                  <span className="text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {successRec} Successful
                  </span>
                  {failedRec > 0 ? (
                    <span className="text-rose-600 flex items-center gap-1 font-semibold">
                      <XCircle className="w-3.5 h-3.5" />
                      {failedRec} Failed / Skipped
                    </span>
                  ) : (
                    <span className="text-slate-400">0 Errors</span>
                  )}
                </div>
              </div>

              {/* 4. Human-Readable Error Summary (Only if returned from API) */}
              {(log.errorSummary || log.errorMessage) && (
                <div className="p-4 rounded-xl bg-amber-50/70 border border-amber-200 text-xs">
                  <h4 className="font-bold text-amber-900 uppercase tracking-wider text-[11px] mb-1 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Error Summary
                  </h4>
                  <p className="text-amber-800 leading-relaxed font-medium">
                    {log.errorSummary || log.errorMessage}
                  </p>
                </div>
              )}

              {/* 5. Collapsible JSON Error Inspector */}
              {(log.errorDetails || log.errorMessage || isFailed || isPartial) && (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div
                    onClick={() => setIsJsonOpen(!isJsonOpen)}
                    className="px-4 py-3 bg-slate-900 text-slate-200 flex items-center justify-between cursor-pointer select-none hover:bg-slate-800 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Code2 className="w-4 h-4 text-purple-400" />
                      <span className="text-xs font-bold font-mono">
                        Raw Error Details / JSON Inspector
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyJson(log.errorDetails || log.errorMessage || log);
                        }}
                        className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[11px] font-mono flex items-center gap-1 transition-colors"
                      >
                        {copiedJson ? (
                          <>
                            <Check className="w-3 h-3 text-emerald-400" />
                            <span className="text-emerald-400">Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>Copy JSON</span>
                          </>
                        )}
                      </button>
                      {isJsonOpen ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </div>

                  {isJsonOpen && (
                    <div className="bg-slate-950 p-4 max-h-72 overflow-y-auto text-xs font-mono text-emerald-400">
                      <pre className="whitespace-pre-wrap word-break-all">
                        {log.errorDetails
                          ? typeof log.errorDetails === 'string'
                            ? log.errorDetails
                            : JSON.stringify(log.errorDetails, null, 2)
                          : log.errorMessage
                          ? JSON.stringify({ message: log.errorMessage, summary: log.errorSummary }, null, 2)
                          : JSON.stringify(log, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50/60 flex items-center justify-between">
          <span className="text-xs text-slate-400">
            Internal Ops & Dev Tool • SAP SFTP Integration
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SapLogDetailDrawer;
